import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Hospital, Building2, Stethoscope, AlertTriangle, Info, MapPin } from 'lucide-react';
import { api } from '../api';

function fmtScore(n) { if (n == null) return '—'; return Number(n).toFixed(1); }
function ConfPill({ c }) {
  const map = { high: 'bg-[#E5F0EA] text-[#2D5F4F]', medium: 'bg-[#FBE6CC] text-[#A06628]', low: 'bg-[#F5EFDD] text-[#7A857F]' };
  return <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${map[c] || map.low}`}>{c || 'low'}</span>;
}

function ScoreBar({ value, max = 100 }) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <div className="w-full h-2.5 bg-[#F0E7D2] rounded overflow-hidden">
      <div className="h-full bg-gradient-to-r from-[#4A7C5E] to-[#2D5F4F]" style={{ width: `${pct}%` }} />
    </div>
  );
}

function ComponentMini({ label, value }) {
  const pct = Math.min(100, Math.max(0, value * 2.5));
  return (
    <div>
      <div className="flex items-baseline justify-between text-[10.5px] text-[#5A6660]">
        <span>{label}</span>
        <span className="font-semibold text-[#1A2622] tabular-nums">{Number(value).toFixed(1)}</span>
      </div>
      <div className="mt-1 h-1.5 bg-[#F0E7D2] rounded overflow-hidden">
        <div className="h-full bg-[#A06628]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function AdmissionsTab({ kind, ccn }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setLoading(true);
    const fn = kind === 'hha' ? api.hhaAdmissions : api.hospiceAdmissions;
    fn(ccn).then((d) => { setData(d); setLoading(false); }).catch((e) => {
      setErr(e?.response?.data?.detail || 'No admissions intel available yet.');
      setLoading(false);
    });
  }, [kind, ccn]);

  if (loading) return <section className="mt-7"><div className="text-[13px] text-[#7A857F]">Loading admissions intelligence…</div></section>;
  if (err) {
    return (
      <section className="mt-7" data-testid="admissions-tab-empty">
        <div className="rounded-xl bg-white border border-[#E5DCC4] p-6 flex items-start gap-3 text-[13px] text-[#5A6660]">
          <AlertTriangle size={16} className="text-[#A06628] shrink-0 mt-0.5" />
          <div>
            <div className="text-[14px] font-semibold text-[#1A2622] mb-1">Admissions intelligence not yet built</div>
            <p className="text-[12.5px] leading-relaxed">{err}</p>
            <p className="text-[11.5px] text-[#7A857F] mt-2">Indexes for all ~12,000 HHAs and ~6,800 hospices are built periodically — an administrator can also trigger a manual refresh from the admin endpoint.</p>
          </div>
        </div>
      </section>
    );
  }

  const score = data.admissions_activity_score;
  const components = data.score_components || {};

  return (
    <section className="mt-7 space-y-5" data-testid="admissions-tab">
      <div className="rounded-xl bg-[#1A2622] text-[#FBF6EC] p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-[#D4A85C] font-semibold">Admissions Activity Score</div>
            <div className="mt-1 font-display text-4xl tabular-nums" data-testid="admissions-score">{fmtScore(score)}<span className="text-[#A8B0AA] text-[16px] ml-2">/ 100</span></div>
          </div>
          <div className="text-right">
            <ConfPill c={data.confidence} />
            <div className="mt-1 text-[10.5px] opacity-75">confidence in feeder mix</div>
          </div>
        </div>
        <div className="mt-3"><ScoreBar value={score} /></div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3 text-[#FBF6EC]">
          <ComponentMini label="Hospital proximity" value={components.hospital_proximity ?? 0} />
          <ComponentMini label="SNF proximity"      value={components.snf_proximity ?? 0} />
          <ComponentMini label="Physician overlap"  value={components.physician_overlap ?? 0} />
          <ComponentMini label="Quality"             value={components.quality ?? 0} />
          <ComponentMini label="Market presence"    value={components.market_presence ?? 0} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
          <div className="text-[10.5px] uppercase tracking-wider text-[#34538F] flex items-center gap-1.5"><Hospital size={11} /> Hospital feeders</div>
          <div className="mt-1 font-display text-2xl tabular-nums" data-testid="adm-hospital-count">{data.hospital_feeder_count}</div>
          <div className="text-[10.5px] text-[#7A857F]">within {data.radius_mi || 25} mi</div>
        </div>
        <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
          <div className="text-[10.5px] uppercase tracking-wider text-[#1A2622] flex items-center gap-1.5"><Building2 size={11} /> SNF feeders</div>
          <div className="mt-1 font-display text-2xl tabular-nums" data-testid="adm-snf-count">{data.snf_feeder_count}</div>
          <div className="text-[10.5px] text-[#7A857F]">within {data.radius_mi || 25} mi</div>
        </div>
        <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
          <div className="text-[10.5px] uppercase tracking-wider text-[#A06628] flex items-center gap-1.5"><Stethoscope size={11} /> Physician influencers</div>
          <div className="mt-1 font-display text-2xl tabular-nums" data-testid="adm-physician-count">{data.physician_influencer_count}</div>
          <div className="text-[10.5px] text-[#7A857F]">aligned with nearby SNFs</div>
        </div>
      </div>

      {data.market_share_pct_estimate != null && (
        <div className="p-4 rounded-xl bg-[#F5EFDD] border border-[#E5DCC4] flex items-baseline gap-3">
          <div className="font-display text-2xl text-[#1A2622] tabular-nums">{data.market_share_pct_estimate}%</div>
          <div className="text-[12px] text-[#3A4A44]">
            Market-share approximation in <strong>{data.city || '—'}, {data.state}</strong>
            <span className="text-[10.5px] text-[#7A857F] block">vs. {data.same_market_agencies} same-market {kind === 'hha' ? 'home-health agencies' : 'hospices'}</span>
          </div>
        </div>
      )}

      {/* HOSPITAL FEEDERS */}
      {data.top_hospital_feeders?.length > 0 && (
        <div className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
          <div className="px-5 py-3 border-b border-[#F0E7D2] flex items-center gap-2 flex-wrap">
            <Hospital size={14} className="text-[#34538F]" />
            <h3 className="font-display text-base font-semibold text-[#1A2622]">Top hospital feeders (estimated)</h3>
            <span className="ml-auto text-[10px] uppercase tracking-wider text-[#7A857F]">distance × capacity × rating</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                <tr>
                  <th className="text-left px-4 py-2.5">Hospital</th>
                  <th className="text-left px-4 py-2.5">Type</th>
                  <th className="text-left px-4 py-2.5">Distance</th>
                  <th className="text-right px-4 py-2.5">Rating</th>
                  <th className="text-right px-4 py-2.5">Activity weight</th>
                  <th className="text-right px-4 py-2.5">Confidence</th>
                </tr>
              </thead>
              <tbody>
                {data.top_hospital_feeders.map((h) => (
                  <tr key={h.ccn} className="border-t border-[#F0E7D2] hover:bg-[#FBF6EC]" data-testid={`adm-feeder-hosp-${h.ccn}`}>
                    <td className="px-4 py-2.5">
                      <Link to={`/hospitals/${h.ccn}`} className="text-[#1A2622] font-medium hover:text-[#34538F]">{h.name}</Link>
                      <div className="text-[10.5px] text-[#7A857F]">{h.city} · CCN {h.ccn}</div>
                    </td>
                    <td className="px-4 py-2.5 text-[11px] text-[#3A4A44]"><div className="truncate max-w-[200px]" title={h.type}>{h.type || '—'}</div></td>
                    <td className="px-4 py-2.5"><span className="inline-flex items-center gap-1 text-[#3A4A44]"><MapPin size={11} className="text-[#34538F]" />{h.distance_mi} mi</span></td>
                    <td className="px-4 py-2.5 text-right">{h.rating != null ? <span className="font-semibold text-[#A06628] tabular-nums">{Number(h.rating).toFixed(1)}<span className="text-[#D4A85C] ml-0.5">★</span></span> : '—'}</td>
                    <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-[#34538F]">{h.estimated_admissions_weight}</td>
                    <td className="px-4 py-2.5 text-right"><ConfPill c={h.confidence} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* SNF FEEDERS */}
      {data.top_snf_feeders?.length > 0 && (
        <div className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
          <div className="px-5 py-3 border-b border-[#F0E7D2] flex items-center gap-2 flex-wrap">
            <Building2 size={14} className="text-[#1A2622]" />
            <h3 className="font-display text-base font-semibold text-[#1A2622]">Top SNF feeders (estimated)</h3>
            <span className="ml-auto text-[10px] uppercase tracking-wider text-[#7A857F]">distance × beds × occupancy × rating</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                <tr>
                  <th className="text-left px-4 py-2.5">SNF</th>
                  <th className="text-right px-4 py-2.5">Beds</th>
                  <th className="text-right px-4 py-2.5">Occupancy</th>
                  <th className="text-left px-4 py-2.5">Distance</th>
                  <th className="text-right px-4 py-2.5">Rating</th>
                  <th className="text-right px-4 py-2.5">Activity weight</th>
                </tr>
              </thead>
              <tbody>
                {data.top_snf_feeders.map((s) => (
                  <tr key={s.ccn} className="border-t border-[#F0E7D2] hover:bg-[#FBF6EC]" data-testid={`adm-feeder-snf-${s.ccn}`}>
                    <td className="px-4 py-2.5">
                      <Link to={`/facilities/${s.ccn}`} className="text-[#1A2622] font-medium hover:text-[#2D5F4F]">{s.name}</Link>
                      <div className="text-[10.5px] text-[#7A857F]">{s.city} · CCN {s.ccn}</div>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{s.beds || '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{s.occupancy_pct != null ? `${Number(s.occupancy_pct).toFixed(0)}%` : '—'}</td>
                    <td className="px-4 py-2.5"><span className="inline-flex items-center gap-1 text-[#3A4A44]"><MapPin size={11} className="text-[#2D5F4F]" />{s.distance_mi} mi</span></td>
                    <td className="px-4 py-2.5 text-right">{s.rating != null ? <span className="font-semibold text-[#A06628] tabular-nums">{Number(s.rating).toFixed(1)}<span className="text-[#D4A85C] ml-0.5">★</span></span> : '—'}</td>
                    <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-[#2D5F4F]">{s.estimated_admissions_weight}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* PHYSICIAN INFLUENCERS */}
      {data.top_physician_influencers?.length > 0 && (
        <div className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
          <div className="px-5 py-3 border-b border-[#F0E7D2] flex items-center gap-2 flex-wrap">
            <Stethoscope size={14} className="text-[#A06628]" />
            <h3 className="font-display text-base font-semibold text-[#1A2622]">Likely physician influencers</h3>
            <span className="ml-auto text-[10px] uppercase tracking-wider text-[#7A857F]">aligned with nearby SNFs (Part B PUF)</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                <tr>
                  <th className="text-left px-4 py-2.5">Physician</th>
                  <th className="text-left px-4 py-2.5">Specialty</th>
                  <th className="text-left px-4 py-2.5">City</th>
                  <th className="text-right px-4 py-2.5">SNF links nearby</th>
                  <th className="text-right px-4 py-2.5">SNF services</th>
                  <th className="text-right px-4 py-2.5">Alignment</th>
                </tr>
              </thead>
              <tbody>
                {data.top_physician_influencers.map((p) => (
                  <tr key={p.npi} className="border-t border-[#F0E7D2] hover:bg-[#FBF6EC]" data-testid={`adm-infl-phys-${p.npi}`}>
                    <td className="px-4 py-2.5">
                      <Link to={`/physicians/${p.npi}`} className="text-[#1A2622] font-medium hover:text-[#A06628]">{p.name}</Link>
                      <div className="text-[10.5px] text-[#7A857F]">NPI {p.npi}</div>
                    </td>
                    <td className="px-4 py-2.5 text-[11.5px] text-[#3A4A44]"><div className="truncate max-w-[180px]" title={p.specialty}>{p.specialty || '—'}</div></td>
                    <td className="px-4 py-2.5 text-[11.5px]">{p.city || '—'}, {p.state || ''}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{p.snf_links_in_area}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{p.snf_services?.toLocaleString() || '—'}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-[#A06628] tabular-nums">{p.alignment_score?.toFixed(0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="rounded-xl bg-[#F5EFDD] border border-[#E5DCC4] p-4 flex items-start gap-2 text-[11.5px] leading-relaxed text-[#3A4A44]">
        <Info size={13} className="text-[#A06628] shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold text-[#1A2622] mb-1">Public-data estimate · not actual admissions</div>
          <p>{data.data_disclaimer}</p>
          <p className="mt-1.5 text-[#5A6660]"><strong>Methodology:</strong> {data.methodology}</p>
          <p className="mt-1.5 text-[#7A857F]"><strong>Source:</strong> {data.source}{data.updated_at && <> · Updated {new Date(data.updated_at).toLocaleDateString()}</>}</p>
        </div>
      </div>
    </section>
  );
}
